import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Store, 
  Search, 
  ToggleLeft, 
  ToggleRight, 
  Trash2, 
  Eye, 
  AlertTriangle,
  Calendar,
  Settings,
  Package,
  Crown,
  X,
  Globe,
  Star
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { getAllStores, updateStore, deleteStore, getUserLimit, updateUserLimit, getAllPlans } from '../../lib/firebaseService';

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [customDomainDialogOpen, setCustomDomainDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeLimit, setStoreLimit] = useState(3);
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [customDomain, setCustomDomain] = useState('');
  const [customDomainEnabled, setCustomDomainEnabled] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadStores();
    loadPlans();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const storesList = await getAllStores();
      setStores(storesList);
    } catch (error) {
      console.error('Mağazalar yüklənə bilmədi:', error);
      toast.error('Mağazalar yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const plansList = await getAllPlans();
      setPlans(plansList);
    } catch (error) {
      console.error('Planlar yüklənə bilmədi:', error);
    }
  };

  const handleToggleStatus = async (store) => {
    setActionLoading(true);
    try {
      const newStatus = !store.is_active;
      await updateStore(store.id, { is_active: newStatus });
      toast.success(newStatus ? 'Mağaza aktivləşdirildi' : 'Mağaza deaktiv edildi');
      loadStores();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (store) => {
    setSelectedStore(store);
    setDeleteDialogOpen(true);
  };

  const handleSettingsClick = async (store) => {
    setSelectedStore(store);
    setSettingsDialogOpen(true);
    
    // Load current limit
    try {
      const limit = await getUserLimit(store.owner_uid);
      setStoreLimit(limit?.max_products || 3);
      setSelectedPlan(store.subscription_plan || 'free');
    } catch (error) {
      console.error('Limit yüklənə bilmədi:', error);
      setStoreLimit(3);
      setSelectedPlan('free');
    }
  };

  const handleSaveSettings = async () => {
    if (!selectedStore) return;
    
    setActionLoading(true);
    try {
      // Update store subscription plan
      await updateStore(selectedStore.id, { 
        subscription_plan: selectedPlan 
      });
      
      // Update user limit
      await updateUserLimit(selectedStore.owner_uid, { 
        max_products: parseInt(storeLimit) 
      });
      
      toast.success('Mağaza ayarları yeniləndi');
      setSettingsDialogOpen(false);
      loadStores();
    } catch (error) {
      console.error('Ayarlar saxlanmadı:', error);
      toast.error('Ayarlar saxlanmadı');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCustomDomain = (store) => {
    setSelectedStore(store);
    setCustomDomain(store.custom_domain || '');
    setCustomDomainEnabled(store.custom_domain_enabled || false);
    setCustomDomainDialogOpen(true);
  };

  const handleSaveCustomDomain = async () => {
    if (!selectedStore) return;
    
    setActionLoading(true);
    try {
      await updateStore(selectedStore.id, { 
        custom_domain: customDomain,
        custom_domain_enabled: customDomainEnabled
      });
      
      toast.success('Custom domain saxlanıldı');
      setCustomDomainDialogOpen(false);
      loadStores();
    } catch (error) {
      console.error('Custom domain saxlanmadı:', error);
      toast.error('Custom domain saxlanmadı');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleShowcase = async (store) => {
    setActionLoading(true);
    try {
      const newShowcaseStatus = !store.is_showcase;
      await updateStore(store.id, { 
        is_showcase: newShowcaseStatus
      });
      
      toast.success(newShowcaseStatus ? 'Mağaza showcase-ə əlavə edildi' : 'Mağaza showcase-dən çıxarıldı');
      loadStores();
    } catch (error) {
      console.error('Showcase dəyişdirilə bilmədi:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedStore) return;
    
    setActionLoading(true);
    try {
      await deleteStore(selectedStore.id);
      toast.success('Mağaza silindi');
      setDeleteDialogOpen(false);
      setSelectedStore(null);
      loadStores();
    } catch (error) {
      toast.error('Mağaza silinə bilmədi');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredStores = stores.filter(store => 
    store.store_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.whatsapp_number?.includes(searchTerm)
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Bilinmir';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('az-AZ');
  };

  const getPlanName = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan?.name || 'Pulsuz';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Yüklənir...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Mağazalar</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            Bütün mağazaları idarə edin ({stores.length} mağaza)
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Mağaza axtar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        <div className="bg-white rounded-xl border border-border p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Store className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold">{stores.length}</p>
              <p className="text-xs md:text-sm text-muted-foreground">Ümumi</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-border p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ToggleRight className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold">{stores.filter(s => s.is_active !== false).length}</p>
              <p className="text-xs md:text-sm text-muted-foreground">Aktiv</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-border p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <ToggleLeft className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold">{stores.filter(s => s.is_active === false).length}</p>
              <p className="text-xs md:text-sm text-muted-foreground">Deaktiv</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stores List - Mobile Friendly */}
      <div className="space-y-3">
        {filteredStores.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-8 text-center">
            <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-muted-foreground">Mağaza tapılmadı</p>
          </div>
        ) : (
          filteredStores.map((store) => (
            <div key={store.id} className="bg-white rounded-xl border border-border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {store.logo_url ? (
                    <img src={store.logo_url} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Store className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">{store.store_name}</h3>
                      {store.subscription_plan && store.subscription_plan !== 'free' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                          <Crown className="w-3 h-3" />
                          {getPlanName(store.subscription_plan)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{store.whatsapp_number}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(store.created_at)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full ${store.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {store.is_active !== false ? 'Aktiv' : 'Deaktiv'}
                      </span>
                      {store.is_showcase && (
                        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                          ⭐ Showcase
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleToggleShowcase(store)}
                  disabled={actionLoading}
                  className={`flex-1 ${store.is_showcase ? 'text-purple-600 border-purple-300' : ''}`}
                >
                  <Star className="w-4 h-4 mr-1" />
                  {store.is_showcase ? 'Showcase' : 'Showcase et'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSettingsClick(store)}
                  className="flex-1"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Ayarlar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenCustomDomain(store)}
                  className="flex-1 text-blue-600"
                >
                  <Globe className="w-4 h-4 mr-1" />
                  Domain
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleToggleStatus(store)}
                  disabled={actionLoading}
                  className={store.is_active !== false ? 'text-orange-600' : 'text-green-600'}
                >
                  {store.is_active !== false ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                </Button>
                <a href={`/store/${store.id}`} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline">
                    <Eye className="w-4 h-4" />
                  </Button>
                </a>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteClick(store)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Mağaza Ayarları
            </DialogTitle>
            <DialogDescription>
              {selectedStore?.store_name} - limit və abunəlik idarəetməsi
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Product Limit */}
            <div>
              <Label htmlFor="limit" className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4" />
                Məhsul Limiti
              </Label>
              <Input
                id="limit"
                type="number"
                min="1"
             
                value={storeLimit}
                onChange={(e) => setStoreLimit(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Bu mağazanın əlavə edə biləcəyi maksimum məhsul sayı
              </p>
            </div>

            {/* Subscription Plan */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4" />
                Abunəlik Planı
              </Label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="plan"
                    value="free"
                    checked={selectedPlan === 'free'}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium">Pulsuz</p>
                    <p className="text-xs text-muted-foreground">Əsas funksiyalar</p>
                  </div>
                </label>
                {plans.map((plan) => (
                  <label key={plan.id} className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="plan"
                      value={plan.id}
                      checked={selectedPlan === plan.id}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{plan.name}</p>
                      <p className="text-xs text-muted-foreground">{plan.price} AZN/ay</p>
                    </div>
                    {plan.max_products && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {plan.max_products} məhsul
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Quick Limit Buttons */}
            <div>
              <Label className="mb-2 block">Sürətli Limit</Label>
              <div className="flex gap-2 flex-wrap">
                {[3, 10, 25, 50, 100, 500].map((num) => (
                  <Button
                    key={num}
                    size="sm"
                    variant={parseInt(storeLimit) === num ? 'default' : 'outline'}
                    onClick={() => setStoreLimit(num)}
                  >
                    {num}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)} className="flex-1">
              Ləğv et
            </Button>
            <Button onClick={handleSaveSettings} disabled={actionLoading} className="flex-1">
              {actionLoading ? 'Saxlanılır...' : 'Yadda saxla'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Mağazanı Sil
            </DialogTitle>
            <DialogDescription>
              <strong>{selectedStore?.store_name}</strong> mağazasını silmək istədiyinizə əminsiniz?
              <br />
              <span className="text-red-600">Bu əməliyyat geri qaytarıla bilməz!</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">
              Ləğv et
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              disabled={actionLoading}
              className="flex-1"
            >
              {actionLoading ? 'Silinir...' : 'Sil'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Domain Dialog */}
      <Dialog open={customDomainDialogOpen} onOpenChange={setCustomDomainDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Custom Domain
            </DialogTitle>
            <DialogDescription>
              <strong>{selectedStore?.store_name}</strong> üçün öz domain-inizi qoşun
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="custom-domain">Custom Domain</Label>
              <Input
                id="custom-domain"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="shop.example.com"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Subdomain və ya öz domeninizi daxil edin (meselen: shop.example.com)
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="domain-enabled"
                checked={customDomainEnabled}
                onChange={(e) => setCustomDomainEnabled(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="domain-enabled" className="cursor-pointer">
                Custom domain aktivdir
              </Label>
            </div>

            {customDomain && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-semibold text-blue-800">📋 DNS Quraşdırma Təlimatı:</p>
                <div className="text-xs text-blue-700 space-y-1">
                  <p>1. Domain provider-inizə girin (GoDaddy, Namecheap və s.)</p>
                  <p>2. DNS Settings bölməsinə keçin</p>
                  <p>3. Yeni CNAME record əlavə edin:</p>
                  <div className="bg-white rounded p-2 font-mono mt-1">
                    <p><strong>Tip:</strong> CNAME</p>
                    <p><strong>Ad:</strong> @ (və ya www)</p>
                    <p><strong>Dəyər:</strong> iveb.az</p>
                  </div>
                  <p className="mt-2">4. Netlify Dashboard-da bu domain-i əlavə edin</p>
                </div>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold mb-2">Mövcud subdomain:</p>
              <code className="text-xs bg-white px-2 py-1 rounded border">
                {selectedStore?.subdomain}.iveb.az
              </code>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCustomDomainDialogOpen(false)} 
              className="flex-1"
              disabled={actionLoading}
            >
              Ləğv et
            </Button>
            <Button 
              onClick={handleSaveCustomDomain} 
              disabled={actionLoading} 
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {actionLoading ? 'Saxlanılır...' : 'Yadda saxla'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
